import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, PieChart as PieIcon, Trophy, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardHeader, Button, fmtInr } from '../ui';

const lakh = (v) => (v >= 1e7 ? `${(v / 1e7).toFixed(1)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(v % 1e5 ? 1 : 0)}L` : v >= 1000 ? `${Math.round(v / 1000)}K` : `${v}`);

const TrendTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="erp-card px-3 py-2 text-xs">
      <p className="font-bold mb-1">{label}</p>
      {payload.map((p) => <p key={p.dataKey} style={{ color: p.fill }}>{p.name}: <b>{fmtInr(p.value)}</b></p>)}
    </div>
  );
};

export const SalesTrend = ({ data }) => (
  <Card className="p-0 erp-rise erp-d2" data-testid="sales-trend-card">
    <CardHeader icon={TrendingUp} title="Sales Trend" subtitle={`Monthly invoicing · FY ${data.fy} vs FY ${data.prev_fy}`} />
    <div className="h-[230px] px-3 pb-2">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.trend || []} barGap={3} barCategoryGap="28%" margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#eef1f7" />
          <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontSize: 11.5, fill: '#6b7590' }} />
          <YAxis tickFormatter={lakh} tickLine={false} axisLine={false} tick={{ fontSize: 11.5, fill: '#6b7590' }} width={44} />
          <Tooltip content={<TrendTip />} cursor={{ fill: '#f3f6fb' }} />
          <Bar dataKey="prev" name={`FY ${data.prev_fy}`} fill="#a9c1f5" radius={[4, 4, 0, 0]} />
          <Bar dataKey="curr" name={`FY ${data.fy}`} fill="#1d3fa6" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
    <div className="flex items-center gap-5 px-5 pb-4 text-xs font-medium" style={{ color: 'var(--erp-muted)' }}>
      <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm" style={{ background: '#a9c1f5' }} /> FY {data.prev_fy}</span>
      <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm" style={{ background: '#1d3fa6' }} /> FY {data.fy}</span>
    </div>
  </Card>
);

export const VerticalDonut = ({ data }) => {
  const verts = data.vertical_split?.length ? data.vertical_split : [{ name: 'No sales yet', value: 100, color: '#e4e9f2' }];
  return (
  <Card className="p-0 erp-rise erp-d3" data-testid="vertical-donut-card">
    <CardHeader icon={PieIcon} title="Business Vertical-wise Sales (YTD)" />
    <div className="flex items-center gap-4 px-5 pb-5">
      <div className="relative h-[190px] w-[190px] flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={verts} dataKey="value" innerRadius={62} outerRadius={90} paddingAngle={2} startAngle={90} endAngle={-270} stroke="none">
              {verts.map((v) => <Cell key={v.name} fill={v.color} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="text-center"><p className="text-lg font-extrabold" style={{ color: 'var(--erp-navy)' }}>{fmtInr(data.sales_ytd)}</p><p className="text-[11px]" style={{ color: 'var(--erp-muted)' }}>Total Sales</p></div>
        </div>
      </div>
      <ul className="flex-1 space-y-3">
        {verts.map((v) => (
          <li key={v.name} className="flex items-center gap-2.5 text-sm">
            <i className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: v.color }} />
            <span className="flex-1 font-medium" style={{ color: '#3b4664' }}>{v.name}</span>
            <span className="font-extrabold">{v.value}%</span>
          </li>
        ))}
      </ul>
    </div>
  </Card>
  );
};

export const TopCustomers = ({ data, go }) => (
  <Card className="p-0 erp-rise erp-d4" data-testid="top-customers-card">
    <CardHeader icon={Trophy} title="Top 5 Customers (YTD)" action={<Button variant="soft" size="sm" onClick={() => go('customers')} data-testid="top-customers-view-all">View All</Button>} />
    {!data.top_customers?.length ? <p className="px-5 pb-6 text-sm" style={{ color: 'var(--erp-muted)' }}>No invoices this financial year yet.</p> : (
    <table className="w-full text-sm mb-2">
      <thead><tr className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--erp-muted)' }}>
        <th className="text-left font-semibold px-5 py-1.5">#</th><th className="text-left font-semibold py-1.5">Customer</th><th className="text-right font-semibold py-1.5">Value</th><th className="text-right font-semibold px-5 py-1.5">Growth</th>
      </tr></thead>
      <tbody>
        {data.top_customers.map((c, i) => (
          <tr key={c.name} className="border-t" style={{ borderColor: '#f0f3f9' }}>
            <td className="px-5 py-2.5 font-bold" style={{ color: 'var(--erp-muted)' }}>{i + 1}</td>
            <td className="py-2.5 font-semibold">{c.name}</td>
            <td className="py-2.5 text-right font-bold">{fmtInr(c.value)}</td>
            <td className={`px-5 py-2.5 text-right font-bold ${c.growth == null ? '' : c.growth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {c.growth == null ? <span style={{ color: 'var(--erp-muted)' }}>new</span> : <span className="inline-flex items-center gap-0.5">{c.growth >= 0 ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{Math.abs(c.growth)}%</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    )}
  </Card>
);
